import React from 'react'
import { Box, Grid, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppButtonIcon, AppCardTitle, AppProviderChips, AppToolbarCreateButton } from '@components'
import { demoBrandColors, dsColorNew, shades } from "@palette"
import UserSettingTierTabButton from "Pages/User/Settings/TierTab/Button"
import { ColorSquare } from "Pages/User/Settings/@components"
import { userSettingTierData } from "Pages/User/Settings/@signals"


const noop = () => {}

const DEMO_TIERS = [
	{ name: 'Premium Competitors', providers: ['DrivePro', 'SpeedCar', 'AutoRent'] },
	{ name: 'Low Cost', providers: ['FleetMax', 'CarFirst', 'RoadPro'] },
	{ name: 'Online Brokers', providers: ['RentalSpot', 'AutoLink', 'BookRide', 'SwiftRent'] },
]

const DEMO_COLORS = [
	{ name: 'PriceFly', color: demoBrandColors.priceFly },
	{ name: 'RentalSpot', color: demoBrandColors.rentalSpot },
	{ name: 'BookRide', color: demoBrandColors.bookRide },
	{ name: 'SwiftFleet', color: demoBrandColors.swiftFleet },
]

// demo values shown as hex codes in the table (data, not styling)
const DEMO_PROVIDER_COLORS = [
	{ name: 'DrivePro', color: demoBrandColors.drivePro },
	{ name: 'SpeedCar', color: demoBrandColors.speedCar },
	{ name: 'FleetMax', color: shades.amber[900] },
	{ name: 'AutoRent', color: shades.red[700] },
]


// A settings list as the real AppCrudTable draws it: card title, search and filter icons with the tab's create
// button, the theme's table head, zebra rows and the count footer; nothing acts
function DemoSettingsTable({ icon, title, action, headers, count, children }) {
	return <Paper elevation={0} sx={{ backgroundColor: dsColorNew.white, p: 2 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5, pb: 1.5 }}>
			<AppCardTitle icon={icon} title={title}/>
			<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, color: dsColorNew.textMuted }}>
				{['magnifying-glass', 'filter'].map(name => <IconButton key={name} tabIndex={-1} sx={{ borderRadius: '8px' }}>
					<IconSeeker icon={name} sx={{ fontSize: 16 }}/>
				</IconButton>)}
				{action}
			</Box>
		</Box>

		<Box sx={{ overflowX: 'auto' }}>
			<Table sx={{
				'& th': { py: 1.5, whiteSpace: 'nowrap' },
				'& td': { fontSize: 13, color: dsColorNew.textDark, borderBottom: `1px solid ${dsColorNew.grayBorder}` },
				// the real cells' Typography variants at the data tables' size (MUIDataTableBodyCell)
				'& td .MuiTypography-subtitle1': { fontSize: 14 },
				'& td .MuiTypography-caption': { fontSize: 12 },
				'& tbody tr:nth-of-type(even)': { backgroundColor: dsColorNew.tableRowAlt },
			}}>
				<TableHead>
					<TableRow>
						{headers.map(({ label, align }) => <TableCell key={label} align={align}>{label}</TableCell>)}
					</TableRow>
				</TableHead>
				<TableBody>{children}</TableBody>
			</Table>
		</Box>

		<Typography sx={{ pt: 1.5, pr: 2, textAlign: 'right', fontSize: 14, color: dsColorNew.textDark }}>1-{count} of {count}</Typography>
	</Paper>
}

function DemoActions() {
	return <Box sx={{ display: 'flex', justifyContent: 'center' }}>
		<AppButtonIcon icon="pen" title="Edit" tabIndex={-1}/>
		<AppButtonIcon icon="trash-can" title="Delete" tabIndex={-1}/>
	</Box>
}

const COLOR_HEADERS = [{ label: 'Name' }, { label: 'Color', align: 'center' }, { label: 'Actions', align: 'center' }]

function DemoColorRow({ item }) {
	return <TableRow>
		<TableCell><Typography variant="subtitle1">{item.name}</Typography></TableCell>
		<TableCell>
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
				<ColorSquare value={item.color}/>
				<Typography variant="caption">{item.color}</Typography>
			</Box>
		</TableCell>
		<TableCell><DemoActions/></TableCell>
	</TableRow>
}


// Only when the Tiers tab (open on arrival) has no rows of its own: with real tiers the tour shows the real list
export default function SettingsDemoData() {
	if (userSettingTierData.value?.length) return null

	return <Grid container spacing={2}>
		<Grid item xs={12}>
			{/* As the real list: title and the tab's create button (the tour points at it) */}
			<DemoSettingsTable
				icon="layer-group"
				title="Your Tiers"
				action={<UserSettingTierTabButton/>}
				headers={[{ label: 'Name' }, { label: 'Providers', align: 'center' }, { label: 'Actions', align: 'center' }]}
				count={DEMO_TIERS.length}
			>
				{DEMO_TIERS.map(tier => <TableRow key={tier.name}>
					<TableCell><Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>{tier.name}</Typography></TableCell>
					<TableCell>
						<AppProviderChips providers={[...tier.providers].sort((a, b) => a.localeCompare(b))} sx={{ justifyContent: 'center' }}/>
					</TableCell>
					<TableCell><DemoActions/></TableCell>
				</TableRow>)}
			</DemoSettingsTable>
		</Grid>

		<Grid item xs={6}>
			<DemoSettingsTable
				icon="palette"
				title="Site Colors"
				action={<AppToolbarCreateButton label="Define Sites Color" tabIndex={-1} onClick={noop}/>}
				headers={COLOR_HEADERS}
				count={DEMO_COLORS.length}
			>
				{DEMO_COLORS.map(item => <DemoColorRow key={item.name} item={item}/>)}
			</DemoSettingsTable>
		</Grid>

		<Grid item xs={6}>
			<DemoSettingsTable
				icon="palette"
				title="Provider Colors"
				action={<AppToolbarCreateButton label="Define Providers Color" tabIndex={-1} onClick={noop}/>}
				headers={COLOR_HEADERS}
				count={DEMO_PROVIDER_COLORS.length}
			>
				{DEMO_PROVIDER_COLORS.map(item => <DemoColorRow key={item.name} item={item}/>)}
			</DemoSettingsTable>
		</Grid>
	</Grid>
}
